import React, { useState, useEffect, useCallback } from 'react';
import { 
  Database, Upload, Search, FileText, Building2, TrendingUp, 
  MessageSquare, Filter, RefreshCw, Eye, Trash2, Download,
  BookOpen, Lightbulb, ChevronDown, ChevronRight, Star,
  AlertCircle, CheckCircle, Clock, BarChart3, MapPin
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

// API 베이스 URL
const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  return `${protocol}//${hostname}:8001`;
};

interface Knowhow {
  id: string;
  title: string;
  agency: string;
  document_type: string;
  description?: string;
  processing_status: string;
  quality_score?: number;
  quality_level?: string;
  keywords?: string[];
  temporal_weight: number;
  view_count: number;
  usage_count: number;
  created_at?: string;
}

interface SearchResult {
  content: string;
  metadata: {
    title?: string;
    agency?: string;
    doc_id?: string;
  };
  score?: number;
}

interface AdviceResult {
  success: boolean;
  advice?: {
    strategic_recommendations?: string[];
    regional_alignment?: any;
    competitive_advantages?: string[];
    risk_mitigation?: string[];
    key_messages?: string[];
    recommended_keywords?: string[];
  };
  company_analysis?: any;
  regional_alignment?: any;
}

const RegionalKnowhow: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'documents' | 'search' | 'advice' | 'stats'>('documents');
  
  // 문서 목록
  const [knowhows, setKnowhows] = useState<Knowhow[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  
  // 필터
  const [agencyFilter, setAgencyFilter] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  
  // 업로드 모달
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    agency: '경상북도경제진흥원',
    document_type: '정책자료',
    description: '',
    author: '',
    publication_date: '',
    source_url: ''
  });
  const [uploading, setUploading] = useState(false);
  
  // 검색
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  
  // 조언
  const [adviceCompanyInfo, setAdviceCompanyInfo] = useState({
    name: '',
    industry: '',
    address: '',
    tags: ''
  });
  const [adviceGrantInfo, setAdviceGrantInfo] = useState({
    title: '',
    organization: '',
    keywords: ''
  });
  const [adviceResult, setAdviceResult] = useState<AdviceResult | null>(null);
  const [generatingAdvice, setGeneratingAdvice] = useState(false);
  
  // 통계
  const [stats, setStats] = useState<any>(null);
  
  // 지역 기관 목록
  const agencies = [
    '경상북도경제진흥원',
    '경북테크노파크',
    '경북연구원',
    '대구테크노파크',
    '대구경북과학기술원',
    '경북IT융합산업기술원',
    '기타'
  ];
  
  const documentTypes = [
    '정책자료',
    '전략보고서',
    '연구보고서',
    '사례연구',
    '가이드라인',
    '제안서템플릿',
    '성공사례',
    '통계자료',
    '백서',
    '뉴스레터',
    '기타'
  ];

  // 문서 목록 로드
  const loadKnowhows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (agencyFilter) params.append('agency', agencyFilter);
      if (docTypeFilter) params.append('document_type', docTypeFilter);
      if (searchText) params.append('search', searchText);
      
      const response = await fetch(
        `${getApiBaseUrl()}/api/regional-knowhow/?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('proposalcraft_access_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setKnowhows(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('노하우 목록 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  }, [agencyFilter, docTypeFilter, searchText]);

  // 통계 로드
  const loadStats = useCallback(async () => {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/regional-knowhow/stats`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('proposalcraft_access_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('통계 로드 오류:', error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'documents') {
      loadKnowhows();
    } else if (activeTab === 'stats') {
      loadStats();
    }
  }, [activeTab, loadKnowhows, loadStats]);

  // 파일 업로드
  const handleUpload = async () => {
    if (!uploadFile || !uploadForm.title) {
      toast.warning('파일과 제목은 필수입니다.');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadForm.title);
      formData.append('agency', uploadForm.agency);
      formData.append('document_type', uploadForm.document_type);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      if (uploadForm.author) formData.append('author', uploadForm.author);
      if (uploadForm.publication_date) formData.append('publication_date', uploadForm.publication_date);
      if (uploadForm.source_url) formData.append('source_url', uploadForm.source_url);
      
      const response = await fetch(
        `${getApiBaseUrl()}/api/regional-knowhow/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('proposalcraft_access_token')}`
          },
          body: formData
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`업로드 완료! 품질 점수: ${data.quality_score}, 키워드 수: ${data.keyword_count}`);
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadForm({
          title: '',
          agency: '경상북도경제진흥원',
          document_type: '정책자료',
          description: '',
          author: '',
          publication_date: '',
          source_url: ''
        });
        loadKnowhows();
      } else {
        const error = await response.json();
        toast.error(`업로드 실패: ${error.detail}`);
      }
    } catch (error) {
      console.error('업로드 오류:', error);
      toast.error('업로드 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  // 시맨틱 검색
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/regional-knowhow/search`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('proposalcraft_access_token')}`
          },
          body: JSON.stringify({
            query: searchQuery,
            top_k: 10,
            include_content: true
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      }
    } catch (error) {
      console.error('검색 오류:', error);
    } finally {
      setSearching(false);
    }
  };

  // 조언 생성
  const handleGenerateAdvice = async () => {
    if (!adviceCompanyInfo.name) {
      toast.warning('기업명은 필수입니다.');
      return;
    }
    
    setGeneratingAdvice(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/regional-knowhow/advice/company`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('proposalcraft_access_token')}`
          },
          body: JSON.stringify({
            company_info: {
              name: adviceCompanyInfo.name,
              industry: adviceCompanyInfo.industry,
              address: adviceCompanyInfo.address,
              tags: adviceCompanyInfo.tags.split(',').map(t => t.trim()).filter(Boolean)
            },
            grant_info: adviceGrantInfo.title ? {
              title: adviceGrantInfo.title,
              organization: adviceGrantInfo.organization,
              keywords: adviceGrantInfo.keywords.split(',').map(k => k.trim()).filter(Boolean)
            } : null
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAdviceResult(data);
      }
    } catch (error) {
      console.error('조언 생성 오류:', error);
    } finally {
      setGeneratingAdvice(false);
    }
  };

  // 문서 삭제
  const handleDelete = async (id: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/regional-knowhow/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('proposalcraft_access_token')}`
          }
        }
      );
      
      if (response.ok) {
        loadKnowhows();
      }
    } catch (error) {
      console.error('삭제 오류:', error);
    }
  };

  // 품질 레벨 뱃지
  const QualityBadge: React.FC<{ level?: string; score?: number }> = ({ level, score }) => {
    const colors: Record<string, string> = {
      excellent: 'bg-green-100 text-green-800',
      good: 'bg-blue-100 text-blue-800',
      fair: 'bg-yellow-100 text-yellow-800',
      poor: 'bg-orange-100 text-orange-800',
      unusable: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[level || 'fair']}`}>
        {score !== undefined ? `${score}점` : level}
      </span>
    );
  };

  // 처리 상태 뱃지
  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const configs: Record<string, { color: string; icon: React.ReactNode }> = {
      pending: { color: 'bg-gray-100 text-gray-800', icon: <Clock className="w-3 h-3" /> },
      processing: { color: 'bg-blue-100 text-blue-800', icon: <RefreshCw className="w-3 h-3 animate-spin" /> },
      parsed: { color: 'bg-yellow-100 text-yellow-800', icon: <FileText className="w-3 h-3" /> },
      vectorized: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3 h-3" /> },
      analyzed: { color: 'bg-purple-100 text-purple-800', icon: <BarChart3 className="w-3 h-3" /> },
      failed: { color: 'bg-red-100 text-red-800', icon: <AlertCircle className="w-3 h-3" /> }
    };
    
    const config = configs[status] || configs.pending;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.icon}
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Database className="w-8 h-8" />
            <h1 className="text-2xl font-bold">지역 노하우 데이터 플랫폼</h1>
          </div>
          <p className="text-teal-100">
            경상북도경제진흥원, 경북테크노파크, 경북연구원 등 지역 지원기관의 전략 및 노하우 관리
          </p>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'documents', label: '문서 관리', icon: FileText },
              { id: 'search', label: 'RAG 검색', icon: Search },
              { id: 'advice', label: 'AI 조언', icon: Lightbulb },
              { id: 'stats', label: '통계', icon: BarChart3 }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* 문서 관리 탭 */}
        {activeTab === 'documents' && (
          <div>
            {/* 툴바 */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex-1 flex gap-2">
                <select
                  value={agencyFilter}
                  onChange={(e) => setAgencyFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">전체 기관</option>
                  {agencies.map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
                <select
                  value={docTypeFilter}
                  onChange={(e) => setDocTypeFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">전체 유형</option>
                  {documentTypes.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="검색..."
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                />
                <button
                  onClick={loadKnowhows}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                문서 업로드
              </button>
            </div>

            {/* 문서 목록 */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">제목</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">기관</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">유형</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">상태</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">품질</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">키워드</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">조회</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">액션</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                        로딩 중...
                      </td>
                    </tr>
                  ) : knowhows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        등록된 노하우 문서가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    knowhows.map((kh) => (
                      <tr key={kh.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{kh.title}</div>
                          {kh.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {kh.description}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{kh.agency}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{kh.document_type}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status={kh.processing_status} />
                        </td>
                        <td className="px-4 py-3">
                          <QualityBadge level={kh.quality_level} score={kh.quality_score} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {kh.keywords?.slice(0, 3).map((kw, i) => (
                              <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                {kw}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <Eye className="w-4 h-4 inline mr-1" />
                          {kh.view_count}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleDelete(kh.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-500">
                총 {total}개 문서
              </div>
            </div>
          </div>
        )}

        {/* RAG 검색 탭 */}
        {activeTab === 'search' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* 검색 패널 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-teal-600" />
                시맨틱 검색
              </h2>
              <div className="space-y-4">
                <div>
                  <textarea
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="검색할 내용을 입력하세요...&#10;예: 경북 지역 R&D 지원 전략"
                    rows={4}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={searching || !searchQuery.trim()}
                  className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {searching ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      검색 중...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      검색
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* 검색 결과 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">검색 결과</h2>
              {searchResults.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  검색 결과가 없습니다.
                </div>
              ) : (
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {searchResults.map((result, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 bg-teal-100 text-teal-800 text-xs rounded">
                          {result.metadata.agency || '기관 미상'}
                        </span>
                        {result.score !== undefined && (
                          <span className="text-sm text-gray-500">
                            유사도: {(result.score * 100).toFixed(1)}%
                          </span>
                        )}
                      </div>
                      {result.metadata.title && (
                        <h3 className="font-medium text-gray-900 mb-1">
                          {result.metadata.title}
                        </h3>
                      )}
                      <p className="text-sm text-gray-600 line-clamp-3">
                        {result.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI 조언 탭 */}
        {activeTab === 'advice' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* 입력 폼 */}
            <div className="space-y-6">
              {/* 기업 정보 */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-teal-600" />
                  기업 정보
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      기업명 *
                    </label>
                    <input
                      type="text"
                      value={adviceCompanyInfo.name}
                      onChange={(e) => setAdviceCompanyInfo(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="(주)테크솔루션"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      산업 분야
                    </label>
                    <input
                      type="text"
                      value={adviceCompanyInfo.industry}
                      onChange={(e) => setAdviceCompanyInfo(prev => ({ ...prev, industry: e.target.value }))}
                      placeholder="IT/소프트웨어"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      소재지
                    </label>
                    <input
                      type="text"
                      value={adviceCompanyInfo.address}
                      onChange={(e) => setAdviceCompanyInfo(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="경상북도 구미시"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      키워드 (쉼표 구분)
                    </label>
                    <input
                      type="text"
                      value={adviceCompanyInfo.tags}
                      onChange={(e) => setAdviceCompanyInfo(prev => ({ ...prev, tags: e.target.value }))}
                      placeholder="AI, 빅데이터, 스마트팩토리"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>

              {/* 공모사업 정보 (선택) */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-teal-600" />
                  공모사업 정보 (선택)
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      사업명
                    </label>
                    <input
                      type="text"
                      value={adviceGrantInfo.title}
                      onChange={(e) => setAdviceGrantInfo(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="2026년 지역특화산업육성사업"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      주관기관
                    </label>
                    <input
                      type="text"
                      value={adviceGrantInfo.organization}
                      onChange={(e) => setAdviceGrantInfo(prev => ({ ...prev, organization: e.target.value }))}
                      placeholder="경북테크노파크"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      키워드 (쉼표 구분)
                    </label>
                    <input
                      type="text"
                      value={adviceGrantInfo.keywords}
                      onChange={(e) => setAdviceGrantInfo(prev => ({ ...prev, keywords: e.target.value }))}
                      placeholder="R&D, 사업화, 기술이전"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={handleGenerateAdvice}
                disabled={generatingAdvice || !adviceCompanyInfo.name}
                className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {generatingAdvice ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    조언 생성 중...
                  </>
                ) : (
                  <>
                    <Lightbulb className="w-5 h-5" />
                    맞춤형 조언 생성
                  </>
                )}
              </button>
            </div>

            {/* 조언 결과 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                AI 조언 결과
              </h2>
              
              {!adviceResult ? (
                <div className="text-center text-gray-500 py-16">
                  기업 정보를 입력하고 조언 생성을 클릭하세요.
                </div>
              ) : !adviceResult.success ? (
                <div className="text-center text-red-500 py-16">
                  조언 생성에 실패했습니다.
                </div>
              ) : (
                <div className="space-y-6 max-h-[600px] overflow-y-auto">
                  {/* 전략적 권장사항 */}
                  {adviceResult.advice?.strategic_recommendations && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">📌 전략적 권장사항</h3>
                      <ul className="space-y-2">
                        {adviceResult.advice.strategic_recommendations.map((rec, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <ChevronRight className="w-4 h-4 mt-0.5 text-teal-500 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 핵심 메시지 */}
                  {adviceResult.advice?.key_messages && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">💬 핵심 메시지</h3>
                      <div className="space-y-2">
                        {adviceResult.advice.key_messages.map((msg, i) => (
                          <div key={i} className="p-3 bg-teal-50 rounded-lg text-sm text-teal-800">
                            {msg}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 경쟁 우위 */}
                  {adviceResult.advice?.competitive_advantages && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">🏆 경쟁 우위</h3>
                      <div className="flex flex-wrap gap-2">
                        {adviceResult.advice.competitive_advantages.map((adv, i) => (
                          <span key={i} className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                            {adv}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 리스크 완화 */}
                  {adviceResult.advice?.risk_mitigation && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">⚠️ 주의사항</h3>
                      <ul className="space-y-1">
                        {adviceResult.advice.risk_mitigation.map((risk, i) => (
                          <li key={i} className="text-sm text-orange-600">
                            • {risk}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 추천 키워드 */}
                  {adviceResult.advice?.recommended_keywords && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">🏷️ 추천 키워드</h3>
                      <div className="flex flex-wrap gap-2">
                        {adviceResult.advice.recommended_keywords.map((kw, i) => (
                          <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 통계 탭 */}
        {activeTab === 'stats' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* 전체 통계 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-teal-600" />
                전체 문서
              </h3>
              <div className="text-4xl font-bold text-teal-600">
                {stats?.total_documents || 0}
              </div>
              <p className="text-sm text-gray-500 mt-1">개 등록됨</p>
            </div>

            {/* 기관별 통계 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-600" />
                기관별 현황
              </h3>
              {stats?.by_agency ? (
                <div className="space-y-2">
                  {Object.entries(stats.by_agency).map(([agency, count]) => (
                    <div key={agency} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{agency}</span>
                      <span className="font-medium">{count as number}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">데이터 없음</p>
              )}
            </div>

            {/* 문서 유형별 통계 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-600" />
                유형별 현황
              </h3>
              {stats?.by_document_type ? (
                <div className="space-y-2">
                  {Object.entries(stats.by_document_type).map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{type}</span>
                      <span className="font-medium">{count as number}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">데이터 없음</p>
              )}
            </div>

            {/* VectorDB 통계 */}
            <div className="bg-white rounded-lg shadow p-6 col-span-full lg:col-span-1">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-teal-600" />
                벡터 DB
              </h3>
              {stats?.vectordb ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">인덱스 문서</span>
                    <span className="font-medium">{stats.vectordb.document_count || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">스토어 타입</span>
                    <span className="font-medium">{stats.vectordb.vector_store_type || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">임베딩 모델</span>
                    <span className="font-medium text-xs">{stats.vectordb.embedding_model || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">LLM 사용</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      stats.vectordb.llm_available 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {stats.vectordb.llm_available ? '가능' : '불가'}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">데이터 없음</p>
              )}
            </div>

            {/* 지역 특화 분야 */}
            <div className="bg-white rounded-lg shadow p-6 col-span-full lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-teal-600" />
                경북 지역별 특화 분야
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { city: '포항', areas: ['철강', '2차전지소재', '수소에너지'] },
                  { city: '구미', areas: ['전자부품', '디스플레이', '반도체'] },
                  { city: '경산', areas: ['바이오', '의료기기', '대학연계'] },
                  { city: '안동', areas: ['바이오한방', '농식품', '문화관광'] },
                  { city: '김천', areas: ['물류', '에너지', '섬유'] },
                  { city: '영주', areas: ['인삼한방', '전통식품', '관광'] }
                ].map(({ city, areas }) => (
                  <div key={city} className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-gray-900 mb-2">{city}</div>
                    <div className="flex flex-wrap gap-1">
                      {areas.map(area => (
                        <span key={area} className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded">
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 업로드 모달 */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">노하우 문서 업로드</h2>
              
              <div className="space-y-4">
                {/* 파일 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    파일 선택 *
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.hwp,.hwpx,.docx,.doc,.txt,.png,.jpg,.jpeg"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    지원 형식: PDF, HWP, DOCX, TXT, 이미지
                  </p>
                </div>

                {/* 제목 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    제목 *
                  </label>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                {/* 기관 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    기관 *
                  </label>
                  <select
                    value={uploadForm.agency}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, agency: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    {agencies.map(a => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>

                {/* 문서 유형 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    문서 유형 *
                  </label>
                  <select
                    value={uploadForm.document_type}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, document_type: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    {documentTypes.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {/* 설명 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    설명
                  </label>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                {/* 작성자 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    작성자
                  </label>
                  <input
                    type="text"
                    value={uploadForm.author}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, author: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                {/* 발행일 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    발행일
                  </label>
                  <input
                    type="date"
                    value={uploadForm.publication_date}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, publication_date: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || !uploadFile || !uploadForm.title}
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      업로드 중...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      업로드
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionalKnowhow;
